import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Loader2,
  CreditCard,
  Globe,
  CheckCircle,
  Info,
} from 'lucide-react';
import { api } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';

interface CreateAddressModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}

interface BankAccount {
  id: string;
  accountNumber: string;
  accountName: string;
  bankName: string;
  networkId: string;
}

interface SupportedChain {
  network: string;
  name: string;
  chainId: number;
  addressType: string;
  tokens: Array<{
    symbol: string;
    address?: string;
    decimals: number;
  }>;
}

export function CreateAddressModal({
  open,
  onClose,
  onSubmit,
}: CreateAddressModalProps) {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [supportedChains, setSupportedChains] = useState<SupportedChain[]>([]);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<string>('');
  const [selectedNetwork, setSelectedNetwork] = useState<string>('');
  const [selectedAsset, setSelectedAsset] = useState<string>('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      loadBankAccounts();
      loadSupportedChains();
    }
  }, [open]);

  const loadBankAccounts = async () => {
    try {
      setLoading(true);
      const response = await api.bankAccounts.list();
      setBankAccounts(response.data.accounts || []);

      // Auto-select if only one account
      if (response.data.accounts?.length === 1) {
        setSelectedBankAccountId(response.data.accounts[0].id);
      }
    } catch (error: any) {
      console.error('Failed to load bank accounts:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to load bank accounts',
        description: error.response?.data?.message || 'Please try again later',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSupportedChains = async () => {
    try {
      const response = await api.staticAddresses.getSupportedChains();
      setSupportedChains(response.data.chains || []);

      // Default to Base network if available
      const baseNetwork = response.data.chains?.find((c: SupportedChain) => c.network === 'BASE');
      if (baseNetwork) {
        setSelectedNetwork('BASE');
        // Default to USDC if available
        const usdc = baseNetwork.tokens.find((t: any) => t.symbol === 'USDC');
        if (usdc) {
          setSelectedAsset('USDC');
        }
      }
    } catch (error: any) {
      console.error('Failed to load supported chains:', error);
      // Use fallback chains if API fails
      setSupportedChains([
        {
          network: 'BASE',
          name: 'Base',
          chainId: 8453,
          addressType: 'EVM',
          tokens: [{ symbol: 'USDC', decimals: 6 }],
        },
        {
          network: 'POLYGON',
          name: 'Polygon',
          chainId: 137,
          addressType: 'EVM',
          tokens: [{ symbol: 'USDC', decimals: 6 }, { symbol: 'USDT', decimals: 6 }],
        },
        {
          network: 'ARB',
          name: 'Arbitrum',
          chainId: 42161,
          addressType: 'EVM',
          tokens: [{ symbol: 'USDC', decimals: 6 }],
        },
        {
          network: 'SOL',
          name: 'Solana',
          chainId: 0,
          addressType: 'SOLANA',
          tokens: [{ symbol: 'USDC', decimals: 6 }],
        },
      ]);
      setSelectedNetwork('BASE');
      setSelectedAsset('USDC');
    }
  };

  const handleNext = () => {
    if (step === 1 && !selectedBankAccountId) {
      toast({
        variant: 'destructive',
        title: 'Please select a bank account',
      });
      return;
    }
    if (step === 2 && !selectedNetwork) {
      toast({
        variant: 'destructive',
        title: 'Please select a network',
      });
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleCreate = async () => {
    try {
      setCreating(true);

      const data = {
        bankAccountId: selectedBankAccountId,
        originNetwork: selectedNetwork,
        originAsset: selectedAsset || undefined,
      };

      await onSubmit(data);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setCreating(false);
    }
  };

  const selectedBankAccount = bankAccounts.find((acc) => acc.id === selectedBankAccountId);
  const selectedChain = supportedChains.find((chain) => chain.network === selectedNetwork);

  const getNetworkIcon = (network: string) => {
    const icons: Record<string, string> = {
      BASE: '🔵',
      POLYGON: '🟣',
      ARB: '🔷',
      OP: '🔴',
      SOL: '🟪',
      AVAX: '🔺',
      BSC: '🟡',
    };
    return icons[network] || '🌐';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Static Deposit Address</DialogTitle>
          <DialogDescription>
            Create a permanent address for receiving crypto payments
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}
            >
              {step > 1 ? <CheckCircle className="h-5 w-5" /> : '1'}
            </div>
            <div className={`w-16 h-0.5 ${step >= 2 ? 'bg-blue-500' : 'bg-gray-200'}`} />
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}
            >
              {step > 2 ? <CheckCircle className="h-5 w-5" /> : '2'}
            </div>
            <div className={`w-16 h-0.5 ${step >= 3 ? 'bg-blue-500' : 'bg-gray-200'}`} />
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}
            >
              3
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            {/* Step 1: Select Bank Account */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="bank-account">Select Bank Account</Label>
                  <Select
                    value={selectedBankAccountId}
                    onValueChange={setSelectedBankAccountId}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choose a bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-4 w-4" />
                            <span>
                              {account.accountNumber} - {account.bankName}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-gray-600 mt-2">
                    Funds will be deposited to this account
                  </p>
                </div>

                {bankAccounts.length === 0 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      You need to add a bank account first.{' '}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          onClose();
                          window.location.href = '/bank-accounts';
                        }}
                      >
                        Add Bank Account
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Step 2: Select Network */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="network">Select Blockchain Network</Label>
                  <Select
                    value={selectedNetwork}
                    onValueChange={(value) => {
                      setSelectedNetwork(value);
                      // Reset asset when network changes
                      const chain = supportedChains.find((c) => c.network === value);
                      if (chain && chain.tokens.length > 0) {
                        setSelectedAsset(chain.tokens[0].symbol);
                      }
                    }}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choose a network" />
                    </SelectTrigger>
                    <SelectContent>
                      {supportedChains.map((chain) => (
                        <SelectItem key={chain.network} value={chain.network}>
                          <div className="flex items-center gap-2">
                            <span>{getNetworkIcon(chain.network)}</span>
                            <span>{chain.name}</span>
                            {chain.addressType === 'EVM' && (
                              <span className="text-xs text-gray-500">(EVM)</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedChain && selectedChain.tokens.length > 1 && (
                  <div>
                    <Label htmlFor="asset">Select Asset (Optional)</Label>
                    <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Choose an asset" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedChain.tokens.map((token) => (
                          <SelectItem key={token.symbol} value={token.symbol}>
                            {token.symbol}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    {selectedChain?.addressType === 'EVM'
                      ? 'This address will work on all EVM-compatible chains (Ethereum, Base, Polygon, etc.)'
                      : `This address is specific to ${selectedChain?.name}`}
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && (
              <div className="space-y-4">
                <h3 className="font-semibold">Confirm Details</h3>

                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Bank Account:</span>
                    <span className="text-sm font-medium">
                      {selectedBankAccount?.accountNumber} ({selectedBankAccount?.bankName})
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Network:</span>
                    <span className="text-sm font-medium">
                      {getNetworkIcon(selectedNetwork)} {selectedChain?.name}
                    </span>
                  </div>
                  {selectedAsset && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Asset:</span>
                      <span className="text-sm font-medium">{selectedAsset}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Address Type:</span>
                    <span className="text-sm font-medium">
                      {selectedChain?.addressType === 'EVM' ? 'Universal (EVM)' : selectedChain?.addressType}
                    </span>
                  </div>
                </div>

                <Alert>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription>
                    This address will be permanent and reusable. Anyone can send {selectedAsset || 'crypto'} to this
                    address, and it will automatically convert to your local currency.
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          {step > 1 && (
            <Button variant="secondary" onClick={handleBack} disabled={creating}>
              Back
            </Button>
          )}
          {step < 3 ? (
            <Button onClick={handleNext}>Next</Button>
          ) : (
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Address'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}